/* eslint-disable */
import { Box, Typography, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { MenuItemLink, MenuProps, useResourceDefinitions, useSidebarState } from 'react-admin'
import { Dot, Xxllnc } from '../components/Icons'
import useAutoLogout from '../hooks/useAutoLogout'
import SubMenu from './SubMenu'
import { border, color, xxllncColor } from './themes'

const Menu = ({ dense = false }: MenuProps): JSX.Element => {
  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name])
  const isSmall = useMediaQuery('(min-width:600px)')
  const [open, setOpen] = useSidebarState()
  const [state, setState] = useState({ menuSettings: false })

  useAutoLogout()

  useEffect(() => {
    if (!isSmall) setOpen(true)
  }, [isSmall, setOpen])

  const handleToggle = (menu: MenuName) => {
    setState(stateItem => ({ ...stateItem, [menu]: !stateItem[menu] }))
  }

  const getMenuItems = (object) => {
    let values = object.filter(resource => resource.icon && !resource.options?.submenu)
    const submenu = object.filter(resource => resource.icon && resource.options?.submenu)

    if (submenu.length > 0) {
      values = [
        ...values,
        {
          name: 'submenu.settings',
          resources: submenu.map(prop => prop.name),
          submenu: [...submenu]
        }
      ]
    }

    return values
  }

  return (
    <StyledBox mt={1} className={classes.root}>
      <div className={classes.branding}>
        <Xxllnc className={classes.logo}  />
        <Typography
          component="span"
          color="textPrimary"
          className={classes.lab}
        >
          KCC
        </Typography>
      </div>
      <div className={classes.spacer}>&nbsp;</div>
      <StyledMenuItemLink
        className={classes.item}
        dense={dense}
        key={'dashboard'}
        primaryText="Dashboard"
        sidebarIsOpen={!open}
        to={`/`}
      />
      {getMenuItems(resources).map(resource => {
        if ('submenu' in resource) {
          return (
            <SubMenu
              key={resource.name}
              handleToggle={() => handleToggle('menuSettings')}
              isOpen={state.menuSettings}
              sidebarIsOpen={!open}
              name={resource.name}
              dense={dense}
            >
              {resource.submenu.map(item => (
                <StyledMenuItemLink
                  className={classes.link}
                  classes={classes}
                  dense={dense}
                  key={item.name}
                  leftIcon={<Dot fill='#fff' />}
                  primaryText={item.options?.label || item.name}
                  sidebarIsOpen={!open}
                  to={`/${item.name}`}
                />
              ))}
            </SubMenu>
          )
        }
        return (
          <StyledMenuItemLink
            className={classes.item}
            dense={dense}
            key={resource.name}
            primaryText={resource.options?.label || resource.name}
            sidebarIsOpen={!open}
            to={`/${resource.name}`}
          />
        )
      })}
    </StyledBox>
  )
}

type MenuName = 'menuSettings'

const PREFIX = 'xxllncMenu'

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  branding: `${PREFIX}-branding`,
  logo: `${PREFIX}-logo`,
  lab: `${PREFIX}-lab`,
  active: `${PREFIX}-active`,
  icon: `${PREFIX}-icon`,
  item: `${PREFIX}-item`,
  spacer: `${PREFIX}-spacer`,
  footer: `${PREFIX}-footer`
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.link}`]: {
    [theme.breakpoints.up('sm')]: {
      color: xxllncColor.light100,
      ['&:hover']: {
        backgroundColor: xxllncColor.dark100,
      },
    }
  },
  [`& .${classes.item}`]: {
    padding: '12px 20px',
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: xxllncColor.dark100,
      }
    }
  },
  [`& .${classes.branding}`]: {
    display: 'flex',
    flexDirection: 'row',
  },
  [`& .${classes.logo}`]: {
    fill: color.common.inactive,
    width: theme.spacing(10),
    marginTop: theme.spacing(1.3),
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(1.5),
  },
  [`& .${classes.lab}`]: {
    backgroundColor: xxllncColor.opDark80,
    color: xxllncColor.opLight60,
    borderRadius: border.radius.lg,
    fontSize: 9,
    fontWeight: 700,
    marginTop: '14px',
    padding: '5px 8px',
  },
  [`& .${classes.spacer}`]: {
    height: 40,
  },  
  [`& .${classes.footer}`]: {
    textAlign: 'center',
    color: '#fff',
    position: 'absolute',
    bottom: 10,
    width: '100%',
    left: 0,
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    '& li': {
      color: '#fff',
      fontSize: '0.9rem',
    },
    '& a': {
      color: '#fff',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}))

const StyledMenuItemLink = styled(MenuItemLink)(({ theme }) => ({
  '&.RaMenuItemLink-active': {
    backgroundColor: xxllncColor.opDark80,
    color: xxllncColor.light100,
  },
}))

export default Menu